import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';

import config from '../../config';
import css from './BookingChangesPayment.module.css';
import StripePaymentElement from '../StripeCheckoutForm/StripePaymentElement';
import { PrimaryButton } from '..';

const stripePromise = loadStripe(config.stripe.publishableKey);

const PaymentFormInner = ({
  values,
  initialValues,
  submitDisabled,
  inProgress,
  onPaymentConfirmed,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const updateErrorMessage = useCallback(message => {
    setErrorMessage(message);
  }, []);

  const handleConfirmPayment = async () => {
    if (!stripe || !elements) {
      updateErrorMessage('Payment form is still loading. Please try again.');
      return;
    }

    setIsConfirming(true);
    updateErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        if (error.type === 'card_error' || error.type === 'validation_error') {
          updateErrorMessage(error.message);
        } else {
          updateErrorMessage('An unexpected error occurred.');
        }
        setIsConfirming(false);
        return;
      }

      await onPaymentConfirmed(values, initialValues, paymentIntent);
    } catch (e) {
      setIsConfirming(false);
    }
  };

  return (
    <div>
      <h3 className={css.heading}>
        <FormattedMessage id="BookingChangesPayment.heading" />
      </h3>
      <p className={css.subheading}>
        <FormattedMessage id="BookingChangesPayment.subheading" />
      </p>

      <div className={css.paymentElementContainer}>
        <StripePaymentElement
          paymentElementId="booking-changes-payment-element"
          showSaveCardCheckbox={false}
          onChange={event => {
            updateErrorMessage(event?.error?.message || null);
          }}
        />
      </div>

      {errorMessage && <div className={css.errorMessage}>{errorMessage}</div>}

      <div className={css.buttonContainer}>
        <PrimaryButton
          type="button"
          isFullWidth
          inProgress={isConfirming || inProgress}
          disabled={submitDisabled || isConfirming || inProgress || !stripe || !elements}
          onClick={handleConfirmPayment}
        >
          <FormattedMessage id="BookingChanges.sendRequest" />
        </PrimaryButton>
      </div>
    </div>
  );
};

const InlinePaymentElement = ({
  clientSecret,
  values,
  initialValues,
  submitDisabled,
  inProgress,
  onPaymentConfirmed,
}) => {
  if (!clientSecret) {
    return null;
  }

  const stripeOptions = {
    clientSecret,
    appearance: config.stripe.appearance,
  };

  return (
    <Elements stripe={stripePromise} options={stripeOptions}>
      <PaymentFormInner
        values={values}
        initialValues={initialValues}
        submitDisabled={submitDisabled}
        inProgress={inProgress}
        onPaymentConfirmed={onPaymentConfirmed}
      />
    </Elements>
  );
};

export default InlinePaymentElement;
